import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Users, FileText, TrendingUp, Clock, CheckCircle } from "lucide-react";
import api from "@/lib/api";

const statusColors = {
    pending_payment: "bg-yellow-100 text-yellow-800",
    pending_approval: "bg-orange-100 text-orange-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    completed: "bg-blue-100 text-blue-800",
};

const statusLabels = {
    pending_payment: "Väntar betalning",
    pending_approval: "Väntar godkännande",
    approved: "Godkänd",
    rejected: "Avvisad",
    completed: "Slutförd",
};

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(console.error).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-8 text-neutral-400">Laddar...</div>;
    if (!stats) return <div className="p-8 text-neutral-400">Kunde inte ladda dashboard</div>;

    const statCards = [
        { icon: BookOpen, label: "Bokningar", value: stats.total_bookings, sub: `${stats.pending_bookings} väntande` },
        { icon: Users, label: "Kontakter", value: stats.total_contacts, sub: `${stats.new_leads} nya leads` },
        { icon: FileText, label: "Offerter", value: stats.total_offers, sub: `${stats.pending_offers} öppna` },
        { icon: TrendingUp, label: "Vunna", value: stats.won_deals, sub: "affärer" },
    ];

    return (
        <div className="p-8" data-testid="admin-dashboard">
            <h1 className="text-2xl font-medium tracking-tight mb-8">Dashboard</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {statCards.map((card, i) => (
                    <Card key={i} className="bg-white border-neutral-200 shadow-none rounded-md" data-testid={`stat-card-${card.label.toLowerCase()}`}>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <card.icon size={18} className="text-neutral-400" />
                                <span className="text-xs uppercase tracking-wider text-neutral-500">{card.label}</span>
                            </div>
                            <div className="text-3xl font-medium tracking-tight">{card.value}</div>
                            <div className="text-xs text-neutral-400 mt-1">{card.sub}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pending Bookings */}
                <Card className="bg-white border-neutral-200 shadow-none rounded-md">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm uppercase tracking-wider text-neutral-500 flex items-center gap-2">
                            <Clock size={16} /> Senaste bokningar
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {stats.recent_bookings?.map((b) => (
                                <Link to="/admin/bookings" key={b.id} className="flex items-center justify-between p-3 border border-neutral-100 rounded hover:bg-neutral-50 transition-colors" data-testid={`recent-booking-${b.id}`}>
                                    <div>
                                        <div className="text-sm font-medium">{b.name}</div>
                                        <div className="text-xs text-neutral-400">{b.date} · Birò {b.model === "small" ? "Small" : "Big"}</div>
                                    </div>
                                    <Badge className={`text-[10px] ${statusColors[b.status] || "bg-neutral-100 text-neutral-600"}`}>{statusLabels[b.status] || b.status}</Badge>
                                </Link>
                            ))}
                            {!stats.recent_bookings?.length && <p className="text-sm text-neutral-400">Inga bokningar ännu</p>}
                        </div>
                    </CardContent>
                </Card>

                {/* Recent Contacts */}
                <Card className="bg-white border-neutral-200 shadow-none rounded-md">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm uppercase tracking-wider text-neutral-500 flex items-center gap-2">
                            <Users size={16} /> Senaste kontakter
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {stats.recent_contacts?.map((c) => (
                                <Link to={`/admin/contacts/${c.id}`} key={c.id} className="flex items-center justify-between p-3 border border-neutral-100 rounded hover:bg-neutral-50 transition-colors" data-testid={`recent-contact-${c.id}`}>
                                    <div>
                                        <div className="text-sm font-medium">{c.name}</div>
                                        <div className="text-xs text-neutral-400">{c.email}</div>
                                    </div>
                                    <Badge variant="outline" className="text-[10px]">{c.pipeline_stage?.replace(/_/g, " ")}</Badge>
                                </Link>
                            ))}
                            {!stats.recent_contacts?.length && <p className="text-sm text-neutral-400">Inga kontakter ännu</p>}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
